import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  Switch as _Switch,
  Alert,
  TextInput,
  Platform,
  Image,
  Modal,
  KeyboardAvoidingView,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Vibrate,
  Lock,
  LogOut,
  Trash2,
  Heart,
  Palette,
  Bell,
  Download,
  Eye,
  Gem,
  Wrench,
  Package,
  ArrowLeft,
  ArrowRight,
  MapPin,
  MoreVertical,
  Plus,
  Star,
  X,
  Upload,
  Trophy,
  Building2,
} from 'lucide-react-native';
import { THEMES, ThemeName } from '@/constants/themes';
import { LinearGradient } from 'expo-linear-gradient';
import { getHapticsEnabled, setHapticsEnabled } from '@/lib/haptics';
import {
  isBiometricAvailable,
  isBiometricLoginEnabled,
  setBiometricLoginEnabled,
  getBiometricLabel,
} from '@/lib/biometricAuth';
import { getHealthSyncConfig, setHealthSyncConfig, type HealthSyncConfig } from '@/lib/healthSync';
import { getNotificationPrefs, setNotificationPrefs, type NotificationPrefs } from '@/lib/notificationPrefs';
import { exportWorkoutData } from '@/lib/dataExport';
import { usePro } from '@/lib/usePro';
import { useSubscription } from '@/hooks/useSubscription';
import { devTierOverride } from '@/lib/devTierOverride';
import type { Tier } from '@/lib/subscription';
import DeveloperToolsAccordion from '@/components/DeveloperToolsAccordion';
import ThemedSwitch from '@/components/ThemedSwitch';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Clipboard from 'expo-clipboard';
import * as Linking from 'expo-linking';
import Constants from 'expo-constants';
import { supabase } from '@/lib/supabase';
import { debugStore } from '@/lib/debugStore';
import { getPrivacySettings, setPrivacySettings, type PrivacySettings } from '@/lib/privacySettings';
import { isPersonalModeEnabled, setPersonalMode } from '@/lib/personalMode';
import { normalizeTier } from '@/lib/subscription';
import { resolveClubOwnerOrg, resolveCoachOwnerOrg } from '@/lib/orgProvisioning';
import { DUEL_LOGO } from '../assets/images';
import { TYPO } from '@/constants/typography';
import { getPasswordStrength, STRENGTH_CONFIG } from '@/lib/passwordStrength';

import { trackEvent } from '@/lib/analytics';
import { captureError } from '@/lib/errorReporting';
import { restorePurchases } from '@/lib/purchases';
import { restoreRevenueCatPurchases } from '@/lib/revenueCat';
import WaitlistModal from '@/components/WaitlistModal';
import type { WaitlistType } from '@/lib/waitlist';

const log = createLogger('SETTINGS');

const ICON_IMAGE = require('../assets/images/icon.png');
const REMOTE_URL =
  'https://upload.wikimedia.org/wikipedia/commons/3/3f/PNG_transparency_demonstration_1.png';

export default function SettingsScreen() {
  const router = useRouter();
  const { signOut, user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const { devOverride, setDevOverride } = usePro();
  const { themeName, setTheme: applyTheme } = useTheme();
  const { tier: subscriptionTier } = useSubscription();
  const [hapticsEnabled, setHapticsEnabledState] = useState(true);
  const [waitlistType, setWaitlistType] = useState<WaitlistType | null>(null);
  const [loading, setLoading] = useState(false);
  const [devTestEmail, setDevTestEmail] = useState('');
  const [computedRedirectTo, setComputedRedirectTo] = useState('');
  const [iconStatus, setIconStatus] = useState('pending');
  const [duelStatus, setDuelStatus] = useState('pending');
  const [remoteStatus, setRemoteStatus] = useState('pending');
  const [iconError, setIconError] = useState('');
  const [duelError, setDuelError] = useState('');
  const [remoteError, setRemoteError] = useState('');
  const [devTier, setDevTier] = useState<Tier | null>(devTierOverride.get());
  const [healthConfig, setHealthConfig] = useState<HealthSyncConfig>({
    enabled: false, syncWorkouts: false, syncBodyweight: false, lastSyncAt: null,
  });
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>({
    duelUpdates: true, freshnessReminders: true, prCelebrations: true, coachMessages: true,
  });
  const [exporting, setExporting] = useState(false);
  const [privacyPrefs, setPrivacyPrefs] = useState<PrivacySettings>({
    showOnLeaderboard: true,
    publicProfile: true,
    showActivityToOthers: true,
  });
  const [dataConsent, setDataConsent] = useState(true);
  const [zenMode, setZenMode] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLabelText, setBiometricLabelText] = useState('Biometric Login');

  // Club Home org ID — resolved lazily for org_pro users
  const [clubOrgId, setClubOrgId] = useState<string | null>(null);
  // Coach Dashboard org ID — resolved lazily for coach_pro users
  const [coachOrgId, setCoachOrgId] = useState<string | null>(null);

  // My Gyms state
  const [myGyms, setMyGyms] = useState<{ gym_id: string; gym_name: string; status: string }[]>([]);
  const [gymsLoading, setGymsLoading] = useState(false);
  const [gymMenuOpen, setGymMenuOpen] = useState<string | null>(null);
  const [showJoinGymModal, setShowJoinGymModal] = useState(false);
  const [joinGymCode, setJoinGymCode] = useState('');
  const [joinGymLoading, setJoinGymLoading] = useState(false);
  const [joinGymError, setJoinGymError] = useState('');

  const loadMyGyms = useCallback(async () => {
    if (!user?.id) return;
    setGymsLoading(true);
    try {
      const { data } = await supabase
        .from('gym_memberships')
        .select('gym_id, status, gyms(name, city)')
        .eq('user_id', user.id)
        .eq('status', 'active');
      if (data) {
        setMyGyms(data.map((m: any) => ({
          gym_id: m.gym_id,
          gym_name: m.gyms?.city
            ? `${m.gyms.name} · ${m.gyms.city}`
            : m.gyms?.name ?? 'Unknown Gym',
          status: m.status,
        })));
      }
    } catch (e) { log.warn('Failed to load gyms', e); }
    setGymsLoading(false);
  }, [user?.id]);

  useEffect(() => { loadMyGyms(); }, [loadMyGyms]);

  // Resolve club org ID for org_pro users (lazy, fire-and-forget)
  useEffect(() => {
    const effectiveTier = (__DEV__ && devTier) ? devTier : normalizeTier(profile as any);
    if (effectiveTier !== 'org_pro' || !user?.id) return;
    resolveClubOwnerOrg(user.id)
      .then((id) => { if (id) setClubOrgId(id); })
      .catch(() => {});
  }, [user?.id, profile, devTier]);

  // Resolve coach org ID for coach_pro users (lazy, fire-and-forget)
  useEffect(() => {
    const effectiveTier = (__DEV__ && devTier) ? devTier : normalizeTier(profile as any);
    if (effectiveTier !== 'coach_pro' || !user?.id) return;
    resolveCoachOwnerOrg(user.id)
      .then((id) => { if (id) setCoachOrgId(id); })
      .catch(() => {});
  }, [user?.id, profile, devTier]);

  const handleSetPrimaryGym = async (gymId: string, gymName: string) => {
    if (!user?.id) return;
    try {
      await supabase.from('profiles').update({
        gym_id: gymId,
        home_gym_id: gymId,
        home_gym_name: gymName,
      }).eq('id', user.id);
      setGymMenuOpen(null);
      if (refreshProfile) await refreshProfile(true);
    } catch (e) { log.warn('Failed to set primary gym', e); }
  };

  const handleLeaveGym = async (gymId: string) => {
    if (!user?.id) return;
    const isPrimary = profile?.gym_id === gymId;
    if (isPrimary) {
      const msg = 'You must switch your primary gym before leaving this one.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Cannot Leave', msg);
      return;
    }
    try {
      await supabase.from('gym_memberships')
        .update({ status: 'inactive' })
        .eq('gym_id', gymId)
        .eq('user_id', user.id);
      setGymMenuOpen(null);
      await loadMyGyms();
    } catch (e) { log.warn('Failed to leave gym', e); }
  };

  const handleJoinGymByCode = async () => {
    if (!user?.id || !joinGymCode.trim()) return;
    setJoinGymLoading(true);
    setJoinGymError('');
    try {
      // Look up gym by join_code
      const { data: gym, error: lookupErr } = await supabase
        .from('gyms')
        .select('id, name')
        .eq('join_code', joinGymCode.trim().toUpperCase())
        .maybeSingle();
      if (lookupErr || !gym) {
        setJoinGymError('No gym found with that code.');
        setJoinGymLoading(false);
        return;
      }
      // Upsert membership
      await supabase.from('gym_memberships').upsert(
        { gym_id: gym.id, user_id: user.id, role: 'member', status: 'active' },
        { onConflict: 'gym_id,user_id' },
      );
      setShowJoinGymModal(false);
      setJoinGymCode('');
      await loadMyGyms();
    } catch (e) {
      setJoinGymError('Something went wrong. Please try again.');
      log.warn('Join gym error', e);
    }
    setJoinGymLoading(false);
  };

  useEffect(() => {
    getHealthSyncConfig().then(setHealthConfig);
    getNotificationPrefs().then(setNotifPrefs);
    getPrivacySettings().then(setPrivacyPrefs);
    // Load data sharing consent from profile
    if (profile?.data_sharing_consent !== undefined) {
      setDataConsent(profile.data_sharing_consent);
    }
    if (profile?.id) {
      isPersonalModeEnabled(profile.id).then(setZenMode);
    }
    // Load biometric state
    (async () => {
      const available = await isBiometricAvailable();
      setBiometricSupported(available);
      if (available) {
        setBiometricEnabled(await isBiometricLoginEnabled());
        setBiometricLabelText(await getBiometricLabel());
      }
    })();
  }, []);

  const handleHealthToggle = async (key: keyof HealthSyncConfig, value: boolean) => {
    const updates: Partial<HealthSyncConfig> = { [key]: value };
    if (key === 'syncWorkouts' || key === 'syncBodyweight') {
      if (value) updates.enabled = true;
    }
    if (key === 'enabled' && !value) {
      updates.syncWorkouts = false;
      updates.syncBodyweight = false;
    }
    const merged = { ...healthConfig, ...updates };
    setHealthConfig(merged);
    await setHealthSyncConfig(updates);
  };

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    return devTierOverride.subscribe(setDevTier);
  }, []);

  const getCurrentRedirectUrl = () => {
    if (Platform.OS === 'web') {
      return 'https://douevenlift.com/reset-password';
    }
    if (Constants.appOwnership === 'expo') {
      return Linking.createURL('reset-password');
    }
    return 'duel://reset-password';
  };

  useEffect(() => {
    setHapticsEnabledState(getHapticsEnabled());
  }, []);

  useEffect(() => {
    setComputedRedirectTo(getCurrentRedirectUrl());
  }, []);

  useEffect(() => {
    if (user?.email) {
      setDevTestEmail(current => current || user.email || '');
    }
  }, [user?.email]);

  const handleHapticsToggle = async (value: boolean) => {
    setHapticsEnabledState(value);
    await setHapticsEnabled(value);
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);

  const handleSignOut = () => {
    if (Platform.OS === 'web') {
      setShowSignOutConfirm(true);
    } else {
      Alert.alert('Sign Out', 'Are you sure you want to sign out?', [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Sign Out',
          style: 'destructive',
          onPress: async () => {
            setLoading(true);
            await signOut();
            setLoading(false);
          },
        },
      ]);
    }
  };

  const confirmSignOut = async () => {
    setShowSignOutConfirm(false);
    setLoading(true);
    await signOut().finally(() => setLoading(false));
  };

  const handleDeleteAccount = () => {
    if (Platform.OS === 'web') {
      setShowDeletePrompt(true);
    } else {
      Alert.alert(
        'Delete Account',
        'This will deactivate your account and anonymize your personal information. Your workout and competition data is retained per our Terms of Service. Contact support to permanently delete all data.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete My Account',
            style: 'destructive',
            onPress: () => setShowDeleteConfirm(true),
          },
        ]
      );
    }
  };

  const confirmDeletePrompt = () => {
    setShowDeletePrompt(false);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      // Anonymize profile data — keep workout/duel data but strip PII
      if (profile?.id) {
        const { error: anonError } = await supabase
          .from('profiles')
          .update({
            display_name: 'Deleted User',
            handle: `deleted_${Date.now()}`,
            avatar_url: null,
            bio: null,
          })
          .eq('id', profile.id);
        if (anonError) {
          log.error('Failed to anonymize profile:', anonError);
        }
      }
      // Sign out (Supabase doesn't have client-side user deletion — admin API required)
      await signOut();
    } catch (err: unknown) {
      log.error('Delete account failed:', err);
      const msg = err instanceof Error ? err.message : 'Failed to delete account';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const handleOpenDebugAuth = () => {
    router.push('/debug-auth');
  };

  const handleOpenPublicSmoke = () => {
    router.push('/(dev)/smoke-public');
  };

  const handleCopyRedirectUrl = async () => {
    try {
      const url = computedRedirectTo || getCurrentRedirectUrl();
      await Clipboard.setStringAsync(url);
      Alert.alert(
        'Copied!',
        `Redirect URL copied to clipboard:\n\n${url}\n\nAdd this to your Supabase Redirect URLs allow-list.`,
        [{ text: 'OK' }]
      );
      log.info('[FORGOT_PASSWORD] Copied redirect URL to clipboard:', url);
    } catch (error: unknown) {
      log.error('[FORGOT_PASSWORD] Failed to copy:', error);
      Alert.alert('Copy Failed', "Couldn't copy to clipboard. Please try again.");
    }
  };

  const testRedirectUrl = async (testRedirectTo: string, label: string) => {
    const trimmedEmail = devTestEmail.trim().toLowerCase();
    if (!trimmedEmail) {
      Alert.alert('Email Required', 'Enter a test email address first');
      return;
    }
    try {
      log.info(`[FORGOT_PASSWORD] Testing redirect URL: ${label}`);
      log.info(`[FORGOT_PASSWORD] URL: ${testRedirectTo}`);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: testRedirectTo,
      });

      if (resetError) {
        log.error(`[FORGOT_PASSWORD] Test failed for ${label}:`, resetError);
        Alert.alert('Test Failed', `${label}\n\nError: ${resetError.message}`);
      } else {
        log.info(`[FORGOT_PASSWORD] Test succeeded for ${label}`);
        Alert.alert('Test Succeeded', `${label}\n\nPassword reset email sent successfully.\n\nCheck your email and verify the link goes to reset-password (not homepage).`);
      }
    } catch (err: unknown) {
      log.error(`[FORGOT_PASSWORD] Test exception for ${label}:`, err);
      Alert.alert('Test Error', `${label}\n\n${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleSimulateRecovery = async () => {
    if (!__DEV__) {
      Alert.alert('Error', 'This feature is only available in development mode');
      return;
    }

    Alert.alert(
      'Simulate Recovery',
      'This will create a test deep link event. Make sure you have a valid session first.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Simulate',
          onPress: async () => {
            const mockUrl = 'duel://reset-password?type=recovery&token_hash=mock_token_12345';
            log.info('[DEBUG] Simulating recovery deep link:', mockUrl);
            debugStore.setDeepLink(mockUrl, {
              queryParams: { type: 'recovery', token_hash: 'mock_token_12345' },
              fragmentParams: {},
              allParams: { type: 'recovery', token_hash: 'mock_token_12345' },
            });
            Alert.alert('Simulated', 'Check console logs for the simulated deep link event');
          },
        },
      ]
    );
  };

  const handleForceLocalSignOut = () => {
    if (!__DEV__) {
      Alert.alert('Error', 'This feature is only available in development mode');
      return;
    }

    Alert.alert(
      'Force Local Sign Out',
      'This will clear local auth state and navigate to welcome WITHOUT calling Supabase signOut. Use this if web/network issues prevent normal sign out.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Force Sign Out',
          style: 'destructive',
          onPress: () => {
            log.info('[DEBUG_AUTH] ========== FORCE LOCAL SIGN OUT ==========');
            log.info('[DEBUG_AUTH] Clearing debug store');
            debugStore.clear();
            log.info('[DEBUG_AUTH] Navigating to welcome screen');
            router.replace('/(auth)/welcome');
            log.info('[DEBUG_AUTH] ========== FORCE SIGN OUT COMPLETE ==========');
            Alert.alert('Success', 'Local state cleared and redirected to welcome');
          },
        },
      ]
    );
  };

  const [showPlanPicker, setShowPlanPicker] = useState(false);

  const handleManageSubscription = () => {
    router.push('/paywall');
  };

  const handleSelectPlan = async (selectedTier: Tier) => {
    if (__DEV__) {
      // In dev mode, set the tier override directly for testing
      await devTierOverride.set(selectedTier === 'free' ? null : selectedTier);
      setDevTier(selectedTier === 'free' ? null : selectedTier);
      void setDevOverride(selectedTier === 'pro' || selectedTier === 'coach_pro' || selectedTier === 'org_pro');
      setShowPlanPicker(false);
      const msg = `Dev tier set to ${selectedTier.toUpperCase()}.`;
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Dev Override', msg);
    } else {
      setShowPlanPicker(false);
      router.push('/paywall');
    }
  };

  return (
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
    <WaitlistModal
      visible={waitlistType !== null}
      type={waitlistType ?? 'user'}
      onClose={() => setWaitlistType(null)}
    />
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
    <View style={[styles.container]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <Pressable onPress={() => router.back()} hitSlop={12} style={{ width: 28 }}>
            <ArrowLeft size={24} color={theme.colors.text} />
          </Pressable>
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>SETTINGS</Text>
          <View style={{ width: 28 }} />
        </View>
      </LinearGradient>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Bell size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>NOTIFICATIONS</Text>
          </View>

          <View style={[styles.settingRow, { borderBottomColor: theme.colors.border }]}>
            <View style={styles.settingLeft}>
              <Vibrate size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Haptics</Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Vibration feedback on actions
                </Text>
              </View>
            </View>
            <ThemedSwitch
              value={hapticsEnabled}
              onValueChange={handleHapticsToggle}
              trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
              thumbColor={theme.colors.text}
            />
          </View>

          {([
            { key: 'duelUpdates' as const, label: 'Duel Updates', desc: 'Challenge invites, results & opponent activity' },
            { key: 'freshnessReminders' as const, label: 'Freshness Reminders', desc: 'Nudges when your lifts are getting stale' },
            { key: 'prCelebrations' as const, label: 'PR Celebrations', desc: 'Get hyped when you hit a personal record' },
            { key: 'coachMessages' as const, label: 'Coach Messages', desc: 'Messages and feedback from your coach' },
          ]).map((item, idx, arr) => (
            <View key={item.key} style={[styles.settingRow, { borderBottomColor: theme.colors.border, borderBottomWidth: idx === arr.length - 1 ? 0 : 1 }]}>
              <View style={styles.settingLeft}>
                <Bell size={20} color={theme.colors.accent} />
                <View style={styles.settingInfo}>
                  <Text style={[styles.settingLabel, { color: theme.colors.text }]}>{item.label}</Text>
                  <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>{item.desc}</Text>
                </View>
              </View>
              <ThemedSwitch
                value={notifPrefs[item.key]}
                onValueChange={async (v) => {
                  const updated = { ...notifPrefs, [item.key]: v };
                  setNotifPrefs(updated);
                  await setNotificationPrefs({ [item.key]: v });
                }}
                trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
                thumbColor={theme.colors.text}
              />
            </View>
          ))}
        </View>

        {/* ── MY GYMS ── */}
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <MapPin size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>MY GYMS</Text>
          </View>

          {gymsLoading ? (
            <ActivityIndicator size="small" color={theme.colors.accent} style={{ paddingVertical: 16 }} />
          ) : myGyms.length === 0 ? (
            <Text style={{ color: theme.colors.textTertiary, fontSize: 13, paddingVertical: 12 }}>
              No gym memberships yet.
            </Text>
          ) : (
            myGyms.map((gym, idx) => {
              const isPrimary = profile?.gym_id === gym.gym_id;
              return (
                <View
                  key={gym.gym_id}
                  style={[styles.settingRow, { borderBottomColor: theme.colors.border, borderBottomWidth: idx === myGyms.length - 1 ? 0 : 1 }]}
                >
                  <View style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-start' }}>
                    <MapPin size={20} color={isPrimary ? theme.colors.accent : theme.colors.textTertiary} style={{ marginTop: 1 }} />
                    <View style={{ flex: 1, marginLeft: 10 }}>
                      <Text style={[styles.settingLabel, { color: theme.colors.text }]} numberOfLines={2}>{gym.gym_name}</Text>
                      {isPrimary && (
                        <View style={{ backgroundColor: theme.colors.accent + '20', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, borderWidth: 1, borderColor: theme.colors.accent, alignSelf: 'flex-start', marginTop: 4 }}>
                          <Text style={{ fontSize: 9, color: theme.colors.accent, fontWeight: '700', letterSpacing: 1 }}>PRIMARY</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  <View style={{ position: 'relative', marginLeft: 8 }}>
                    <TouchableOpacity onPress={() => setGymMenuOpen(gymMenuOpen === gym.gym_id ? null : gym.gym_id)} hitSlop={8}>
                      <MoreVertical size={18} color={theme.colors.textTertiary} />
                    </TouchableOpacity>
                    {gymMenuOpen === gym.gym_id && (
                      <View style={{
                        position: 'absolute', right: 0, top: 28, zIndex: 100,
                        backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border,
                        borderRadius: 2, minWidth: 160, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 8, elevation: 5,
                      }}>
                        {!isPrimary && (
                          <TouchableOpacity
                            style={{ paddingVertical: 12, paddingHorizontal: 14 }}
                            onPress={() => handleSetPrimaryGym(gym.gym_id, gym.gym_name)}
                          >
                            <Text style={{ color: theme.colors.text, fontSize: 14, fontWeight: '600' }}>Set as Primary</Text>
                          </TouchableOpacity>
                        )}
                        {!isPrimary && (
                          <TouchableOpacity
                            style={{ paddingVertical: 12, paddingHorizontal: 14, borderTopWidth: 1, borderTopColor: theme.colors.border }}
                            onPress={() => handleLeaveGym(gym.gym_id)}
                          >
                            <Text style={{ color: '#ff4444', fontSize: 14, fontWeight: '600' }}>Leave Gym</Text>
                          </TouchableOpacity>
                        )}
                        {isPrimary && (
                          <View style={{ paddingVertical: 12, paddingHorizontal: 14 }}>
                            <Text style={{ color: theme.colors.textTertiary, fontSize: 13 }}>Switch primary to leave</Text>
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                </View>
              );
            })
          )}

          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, borderTopWidth: myGyms.length > 0 ? 1 : 0, borderTopColor: theme.colors.border, marginTop: 4 }}
            onPress={() => router.push('/(profile)/change-gym')}
          >
            <Plus size={16} color={theme.colors.accent} />
            <Text style={{ color: theme.colors.accent, fontSize: 14, fontWeight: '700' }}>Add Another Gym</Text>
          </TouchableOpacity>

          {((__DEV__ && devTier) ? devTier : normalizeTier(profile as any)) === 'coach_pro' && (
            <>
              {/* Coach Dashboard entry point — Coach Tier V1 */}
              <TouchableOpacity
                style={[styles.settingRow, { borderTopWidth: 1, borderTopColor: theme.colors.border }]}
                onPress={() => {
                  if (coachOrgId) {
                    router.push(`/coach-dashboard?orgId=${coachOrgId}` as any);
                  } else {
                    router.push('/coach-dashboard' as any);
                  }
                }}>
                <View style={styles.settingLeft}>
                  <Building2 size={20} color={theme.colors.textSecondary} />
                  <View style={styles.settingInfo}>
                    <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Coach Dashboard</Text>
                    <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                      Clients, programs, and schedule
                    </Text>
                  </View>
                </View>
                <ArrowRight size={18} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </>
          )}

          {((__DEV__ && devTier) ? devTier : normalizeTier(profile as any)) === 'org_pro' && (
            <>
              {/* Club Home entry point — Phase 4A */}
              <TouchableOpacity
                style={[styles.settingRow, { borderTopWidth: 1, borderTopColor: theme.colors.border }]}
                onPress={() => {
                  if (clubOrgId) {
                    router.push(`/club-home?orgId=${clubOrgId}` as any);
                  } else {
                    router.push('/club-home' as any);
                  }
                }}>
                <View style={styles.settingLeft}>
                  <Building2 size={20} color={theme.colors.textSecondary} />
                  <View style={styles.settingInfo}>
                    <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Your Organization</Text>
                    <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                      Club home, roster, and leaderboard
                    </Text>
                  </View>
                </View>
                <ArrowRight size={18} color={theme.colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.settingRow, { borderTopWidth: 1, borderTopColor: theme.colors.border }]}
                onPress={() => router.push('/gym-branding' as any)}>
                <View style={styles.settingLeft}>
                  <Palette size={20} color={theme.colors.textSecondary} />
                  <View style={styles.settingInfo}>
                    <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Customize Branding</Text>
                    <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                      Logo, colors, and TV mode display
                    </Text>
                  </View>
                </View>
                <ArrowRight size={18} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </>
          )}

          <TouchableOpacity
            style={[styles.settingRow, { borderBottomWidth: 0, borderTopWidth: 1, borderTopColor: theme.colors.border }]}
            onPress={() => router.push('/(gym)/claim' as any)}
          >
            <View style={styles.settingLeft}>
              <MapPin size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Claim Your Gym</Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Register as a gym owner
                </Text>
              </View>
            </View>
            <ArrowRight size={18} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        </View>

        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Package size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>YOUR DATA</Text>
          </View>
          <TouchableOpacity
            style={[styles.settingRow, { borderBottomWidth: 0 }]}
            disabled={exporting}
            onPress={async () => {
              if (!user?.id) return;
              setExporting(true);
              try {
                await exportWorkoutData(user.id, subscriptionTier);
              } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Export failed';
                Platform.OS === 'web' ? alert(msg) : Alert.alert('Export Error', msg);
              } finally {
                setExporting(false);
              }
            }}>
            <View style={styles.settingLeft}>
              <Download size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>
                  {exporting ? 'Generating...' : 'Export My Data'}
                </Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Download all your workout data as CSV
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        </View>

        {/* ── TOOLS ── */}
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Upload size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>TOOLS</Text>
          </View>
          {/* Import Workout History — free for all tiers, helps switchers migrate */}
          <TouchableOpacity
            style={[styles.settingRow, { borderBottomWidth: 0 }]}
            onPress={() => router.push('/import-data' as any)}>
            <View style={styles.settingLeft}>
              <Upload size={20} color={theme.colors.textSecondary} />
              <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Import Workout History</Text>
            </View>
            <ArrowRight size={18} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* ── BASELINE ── Show only when baseline is not complete */}
        {(!profile?.duel_standard_strength_kg || profile.duel_standard_strength_kg === 0) && (
          <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
            <View style={styles.sectionHeader}>
              <Trophy size={16} color={theme.colors.accent} />
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>GETTING STARTED</Text>
            </View>
            <TouchableOpacity
              onPress={async () => {
                if (profile?.id) {
                  await AsyncStorage.removeItem(`duel:baseline_prompt_dismissed:${profile.id}`);
                }
                router.push('/(tabs)/record' as any);
              }}
              style={[styles.settingRow, { borderBottomWidth: 0, backgroundColor: theme.colors.accent + '15' }]}
              accessibilityRole="button"
              accessibilityLabel="Complete your baseline to unlock your Strength Score">
              <View style={styles.settingLeft}>
                <Trophy size={20} color={theme.colors.accent} />
                <View style={styles.settingInfo}>
                  <Text style={[styles.settingLabel, { color: theme.colors.accent, fontWeight: '700' }]}>
                    ⚡ Complete Your Baseline
                  </Text>
                  <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                    Log 5 core lifts to unlock your Strength Score
                  </Text>
                </View>
              </View>
              <ArrowRight size={18} color={theme.colors.accent} />
            </TouchableOpacity>
          </View>
        )}

        {/* ── PRO FEATURES ── */}
        {((__DEV__ && devTier) ? devTier : normalizeTier(profile as any)) !== 'free' && (
          <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
            <View style={styles.sectionHeader}>
              <Trophy size={16} color={theme.colors.textTertiary} />
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>PRO FEATURES</Text>
            </View>
            <TouchableOpacity
              style={[styles.settingRow, { borderBottomWidth: 0 }]}
              onPress={() => router.push('/competition-prep' as any)}>
              <View style={styles.settingLeft}>
                <Trophy size={20} color={theme.colors.textSecondary} />
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Competition Prep</Text>
              </View>
              <ArrowRight size={18} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Palette size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>APPEARANCE</Text>
          </View>

          <Text style={[styles.settingLabel, { color: theme.colors.text, marginBottom: 12 }]}>Theme</Text>
          <View style={styles.themeGrid}>
            {(Object.keys(THEMES) as ThemeName[]).map((key) => {
              const t = THEMES[key];
              const isActive = themeName === key;
              return (
                <TouchableOpacity
                  key={key}
                  style={[
                    styles.themeOption,
                    {
                      borderColor: isActive ? theme.colors.accent : theme.colors.border,
                      backgroundColor: isActive ? theme.colors.surfaceVariant : 'transparent',
                    },
                  ]}
                  onPress={() => { applyTheme(key); trackEvent('theme_changed', { theme: key }); }}>
                  <View style={styles.themeSwatches}>
                    <View style={[styles.themeSwatch, { backgroundColor: t.colors.background }]} />
                    <View style={[styles.themeSwatch, { backgroundColor: t.colors.accent }]} />
                    <View style={[styles.themeSwatch, { backgroundColor: t.colors.text }]} />
                  </View>
                  <Text
                    style={[
                      styles.themeLabel,
                      { color: isActive ? theme.colors.accent : theme.colors.textSecondary },
                    ]}
                    numberOfLines={1}>
                    {t.displayName}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionIcon}>❤️</Text>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Health & Fitness</Text>
          </View>

          <View style={[styles.settingRow, { borderBottomColor: theme.colors.border }]}>
            <View style={styles.settingLeft}>
              <Heart size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Sync Workouts</Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Write workouts to Apple Health / Google Fit
                </Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <View style={{ backgroundColor: theme.colors.accent + '20', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 2 }}>
                <Text style={{ color: theme.colors.accent, fontSize: 10, fontWeight: '700' }}>SOON</Text>
              </View>
              <ThemedSwitch
                value={healthConfig.syncWorkouts}
                onValueChange={(v) => handleHealthToggle('syncWorkouts', v)}
                trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
                thumbColor={theme.colors.text}
              />
            </View>
          </View>

          <View style={[styles.settingRow, { borderBottomWidth: 0 }]}>
            <View style={styles.settingLeft}>
              <Heart size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Import Bodyweight</Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Read bodyweight from Health app
                </Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <View style={{ backgroundColor: theme.colors.accent + '20', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 2 }}>
                <Text style={{ color: theme.colors.accent, fontSize: 10, fontWeight: '700' }}>SOON</Text>
              </View>
              <ThemedSwitch
                value={healthConfig.syncBodyweight}
                onValueChange={(v) => handleHealthToggle('syncBodyweight', v)}
                trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
                thumbColor={theme.colors.text}
              />
            </View>
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Eye size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>PRIVACY</Text>
          </View>

          {([
            { key: 'showOnLeaderboard' as const, label: 'Show on Leaderboard', desc: 'Appear in public rankings' },
            { key: 'publicProfile' as const, label: 'Public Profile', desc: 'Let others view your profile' },
            { key: 'showActivityToOthers' as const, label: 'Show Activity', desc: 'Others can see your workout activity' },
          ]).map((item, idx, arr) => (
            <View key={item.key} style={[styles.settingRow, { borderBottomColor: theme.colors.border, borderBottomWidth: idx === arr.length - 1 ? 0 : 1 }]}>
              <View style={styles.settingLeft}>
                <Lock size={20} color={theme.colors.accent} />
                <View style={styles.settingInfo}>
                  <Text style={[styles.settingLabel, { color: theme.colors.text }]}>{item.label}</Text>
                  <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>{item.desc}</Text>
                </View>
              </View>
              <ThemedSwitch
                value={privacyPrefs[item.key]}
                onValueChange={async (v) => {
                  const updated = { ...privacyPrefs, [item.key]: v };
                  setPrivacyPrefs(updated);
                  await setPrivacySettings({ [item.key]: v });
                }}
                trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
                thumbColor={theme.colors.text}
              />
            </View>
          ))}

          <View style={[styles.settingRow, { borderBottomColor: theme.colors.border, borderBottomWidth: 0 }]}>
            <View style={styles.settingLeft}>
              <Lock size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Aggregate Research Data</Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>Allow anonymized data in aggregate research</Text>
              </View>
            </View>
            <ThemedSwitch
              value={dataConsent}
              onValueChange={async (v) => {
                setDataConsent(v);
                try {
                  await supabase
                    .from('profiles')
                    .update({ data_sharing_consent: v })
                    .eq('id', user?.id);
                } catch (e) {
                  captureError(e as Error, { source: 'data_consent_toggle' });
                }
              }}
              trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
              thumbColor={theme.colors.text}
            />
          </View>

          <View style={[styles.settingRow, { borderBottomColor: theme.colors.border, borderBottomWidth: 0 }]}>
            <View style={styles.settingLeft}>
              <Lock size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Zen Mode</Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Hide leaderboard, rank, and competitive elements. Focus on your personal progress.
                </Text>
              </View>
            </View>
            <ThemedSwitch
              value={zenMode}
              onValueChange={async (val) => {
                setZenMode(val);
                if (profile?.id) await setPersonalMode(profile.id, val);
              }}
              trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
              thumbColor={theme.colors.text}
            />
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Lock size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>PRIVACY & ACCOUNT</Text>
          </View>

          {biometricSupported && (
            <View style={[styles.settingRow, { borderBottomColor: theme.colors.border }]}>
              <View style={styles.settingLeft}>
                <Lock size={20} color={theme.colors.accent} />
                <View style={styles.settingInfo}>
                  <Text style={[styles.settingLabel, { color: theme.colors.text }]}>
                    Enable {biometricLabelText}
                  </Text>
                  <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                    Sign in with biometrics next time
                  </Text>
                </View>
              </View>
              <ThemedSwitch
                value={biometricEnabled}
                onValueChange={async (val) => {
                  setBiometricEnabled(val);
                  await setBiometricLoginEnabled(val);
                }}
                trackColor={{ false: theme.colors.surfaceVariant, true: theme.colors.accent }}
                thumbColor={theme.colors.text}
              />
            </View>
          )}

          {!showPasswordChange ? (
            <TouchableOpacity
              style={[styles.settingRow, { borderBottomWidth: 0 }]}
              onPress={() => setShowPasswordChange(true)}>
              <View style={styles.settingLeft}>
                <Lock size={20} color={theme.colors.accent} />
                <View style={styles.settingInfo}>
                  <Text style={[styles.settingLabel, { color: theme.colors.text }]}>
                    Change Password
                  </Text>
                  <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                    Update your account password
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          ) : (
            <View style={{ padding: 16, gap: 12 }}>
              <Text style={[styles.settingLabel, { color: theme.colors.text }]}>Change Password</Text>
              <TextInput
                style={[styles.devInput, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border, color: theme.colors.text }]}
                placeholder="Current password"
                placeholderTextColor={theme.colors.textTertiary}
                secureTextEntry
                value={currentPassword}
                onChangeText={setCurrentPassword}
              />
              <TextInput
                style={[styles.devInput, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border, color: theme.colors.text }]}
                placeholder="New password"
                placeholderTextColor={theme.colors.textTertiary}
                secureTextEntry
                value={newPassword}
                onChangeText={setNewPassword}
              />
              {newPassword.length > 0 && (() => {
                const strength = getPasswordStrength(newPassword);
                if (strength.level === 'none') return null;
                const cfg = STRENGTH_CONFIG[strength.level];
                return (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <View style={{ flexDirection: 'row', flex: 1, gap: 4, height: 4 }}>
                      {[1, 2, 3, 4].map((i) => (
                        <View
                          key={i}
                          style={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: strength.score >= i ? cfg.color : theme.colors.surfaceVariant }}
                        />
                      ))}
                    </View>
                    <Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 0.8, color: cfg.color, width: 80, textAlign: 'right' }}>
                      {cfg.label}
                    </Text>
                  </View>
                );
              })()}
              <TextInput
                style={[styles.devInput, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border, color: theme.colors.text }]}
                placeholder="Confirm new password"
                placeholderTextColor={theme.colors.textTertiary}
                secureTextEntry
                value={confirmPassword}
                onChangeText={setConfirmPassword}
              />
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <TouchableOpacity
                  style={{ flex: 1, paddingVertical: 12, borderRadius: 0, borderWidth: 1, borderColor: theme.colors.border, alignItems: 'center' }}
                  onPress={() => { setShowPasswordChange(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}>
                  <Text style={{ color: theme.colors.textSecondary, fontWeight: '600' }}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{ flex: 1, paddingVertical: 12, borderRadius: 0, backgroundColor: theme.colors.accent, alignItems: 'center' }}
                  onPress={async () => {
                    if (!currentPassword) {
                      Alert.alert('Heads Up', 'Please enter your current password.');
                      return;
                    }
                    if (newPassword.length < 8) {
                      Alert.alert('Heads Up', 'Password must be at least 8 characters with letters and numbers.');
                      return;
                    }
                    if (!/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
                      Alert.alert('Heads Up', 'Password must contain at least one letter and one number.');
                      return;
                    }
                    if (newPassword !== confirmPassword) {
                      Alert.alert('Heads Up', 'Passwords do not match.');
                      return;
                    }
                    const { error } = await supabase.auth.updateUser({ password: newPassword });
                    if (error) {
                      console.error('Password update error:', error.message);
                      Alert.alert('Oops', "Couldn't update your password. Please try again.");
                    } else {
                      Alert.alert('Success', 'Your password has been updated.');
                      setShowPasswordChange(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }
                  }}>
                  <Text style={{ color: theme.colors.accentForeground, fontWeight: '700' }}>Update</Text>
                </TouchableOpacity>
              </View>
              <TouchableOpacity
                onPress={async () => {
                  const email = user?.email;
                  if (!email) return;
                  const { error } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: 'https://douevenlift.com/reset-password',
                  });
                  if (error) { console.error('Reset password error:', error.message); Alert.alert('Oops', "Couldn't send the reset link. Please try again."); }
                  else { Alert.alert('Reset Link Sent', `A password reset link has been sent to ${email}.`); }
                }}>
                <Text style={{ color: theme.colors.textTertiary, fontSize: 12, textAlign: 'center' }}>
                  Forgot your current password? Send reset link
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <View style={styles.sectionHeader}>
            <Gem size={16} color={theme.colors.textTertiary} />
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>SUBSCRIPTION</Text>
          </View>

          <TouchableOpacity
            style={[styles.settingRow, { borderBottomWidth: 0 }]}
            onPress={handleManageSubscription}>
            <View style={styles.settingLeft}>
              <Lock size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>
                  Manage Subscription
                </Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Current plan: {({ free: 'BASE', pro: 'PRO', coach_pro: 'COACH', org_pro: 'CLUB' } as Record<string, string>)[(__DEV__ && devTier) ? devTier : normalizeTier(profile as any)] ?? 'BASE'}
                </Text>
              </View>
            </View>
          </TouchableOpacity>

          {Platform.OS !== 'web' && (
          <TouchableOpacity
            style={[styles.settingRow, { borderBottomWidth: 0 }]}
            disabled={loading}
            onPress={async () => {
              if (!user?.id) return;
              setLoading(true);
              try {
                // Use RC restore on native (talks to Apple/Google, syncs entitlements to DB)
                // Fall back to DB-only restore on web
                const result = Platform.OS !== 'web'
                  ? await restoreRevenueCatPurchases()
                  : await restorePurchases(user.id);
                if (result.restored) {
                  if (refreshProfile) await refreshProfile(true);
                  Alert.alert('Restored', `Your ${result.tier} subscription has been restored.`);
                } else {
                  Alert.alert('No Purchases Found', 'No active subscription found for this account.');
                }
              } catch {
                Alert.alert('Error', 'Failed to restore purchases. Please try again.');
              } finally {
                setLoading(false);
              }
            }}>
            <View style={styles.settingLeft}>
              <Download size={20} color={theme.colors.accent} />
              <View style={styles.settingInfo}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>
                  {loading ? 'Restoring…' : 'Restore Purchases'}
                </Text>
                <Text style={[styles.settingDescription, { color: theme.colors.textSecondary }]}>
                  Restore a previously purchased subscription
                </Text>
              </View>
            </View>
          </TouchableOpacity>
          )}
        </View>

        {/* Waitlist / Community */}
        <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>COMMUNITY</Text>
          {[
            { label: 'Invite a Friend', desc: 'Share DUEL with someone who lifts', action: () => { import('react-native').then(rn => rn.Share.share({ message: 'Check out DUEL — the competitive strength app. Join here: https://douevenlift.com', url: 'https://douevenlift.com' })); } },
            { label: 'Coach Waitlist', desc: 'Personal trainers — get early access', action: () => setWaitlistType('coach') },
            { label: 'Club Waitlist', desc: 'Gyms, schools & sports orgs', action: () => setWaitlistType('club') },
          ].map(item => (
            <TouchableOpacity key={item.label} style={styles.settingRow} onPress={item.action}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.settingLabel, { color: theme.colors.text }]}>{item.label}</Text>
                <Text style={{ fontSize: 12, color: theme.colors.textTertiary, marginTop: 1 }}>{item.desc}</Text>
              </View>
              <Text style={{ color: theme.colors.accent, fontSize: 13 }}>→</Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={{ alignItems: 'center', paddingVertical: 16 }}
          onPress={handleSignOut}
          disabled={loading}>
          <Text style={{ fontSize: 15, fontWeight: '600', color: theme.colors.textSecondary }}>
            {loading ? 'Signing Out...' : 'Sign Out'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={{ alignItems: 'center', paddingVertical: 12, marginBottom: 16 }}
          onPress={handleDeleteAccount}>
          <Text style={{ fontSize: 13, fontWeight: '500', color: theme.colors.textTertiary }}>Delete Account</Text>
        </TouchableOpacity>

        {showDeleteConfirm && (
          <View style={[styles.section, { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: '#ff4444', marginBottom: 16 }]}>
            <Text style={{ color: theme.colors.text, fontWeight: '700', fontSize: 16, marginBottom: 8 }}>
              Confirm Account Deactivation
            </Text>
            <Text style={{ color: theme.colors.textSecondary, fontSize: 13, marginBottom: 16, lineHeight: 18 }}>
              Your profile will be anonymized and you will be signed out. Workout and competition history is retained per our Terms of Service. Type DELETE to confirm.
            </Text>
            <TextInput
              style={[styles.devInput, { backgroundColor: theme.colors.surfaceVariant, borderColor: deleteConfirmText === 'DELETE' ? '#ff4444' : theme.colors.border, color: theme.colors.text, textAlign: 'center', fontSize: 16, fontWeight: '700', letterSpacing: 2 }]}
              placeholder="Type DELETE"
              placeholderTextColor={theme.colors.textTertiary}
              value={deleteConfirmText}
              onChangeText={setDeleteConfirmText}
              autoCapitalize="characters"
            />
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 0, borderWidth: 1, borderColor: theme.colors.border, alignItems: 'center' }}
                onPress={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}>
                <Text style={{ color: theme.colors.textSecondary, fontWeight: '600' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 12, borderRadius: 0, backgroundColor: deleteConfirmText === 'DELETE' ? '#ff4444' : theme.colors.surfaceVariant, alignItems: 'center', opacity: deleteConfirmText === 'DELETE' ? 1 : 0.4 }}
                onPress={handleConfirmDelete}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}>
                <Text style={{ color: deleteConfirmText === 'DELETE' ? '#fff' : theme.colors.textTertiary, fontWeight: '700' }}>
                  {deletingAccount ? 'Deleting...' : 'Delete Account'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <DeveloperToolsAccordion>
          <View style={styles.devToolsSection}>
            <View style={styles.sectionHeader}>
              <Wrench size={16} color={theme.colors.textTertiary} />
              <Text style={[styles.devToolsTitle, { color: theme.colors.text }]}>
                Developer Tools
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.devPrimaryButton, { backgroundColor: theme.colors.accent }]}
              onPress={() => router.push('/(dev)/admin-dashboard')}>
              <Text style={[styles.devPrimaryButtonText, { color: theme.colors.accentForeground }]}>
                Admin Dashboard
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.devPrimaryButton, { backgroundColor: theme.colors.accent }]}
              onPress={() => router.push('/org-dashboard' as any)}>
              <Text style={[styles.devPrimaryButtonText, { color: theme.colors.accentForeground }]}>
                Club Dashboard
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.devPrimaryButton, { backgroundColor: theme.colors.accent }]}
              onPress={handleOpenDebugAuth}>
              <Text style={[styles.devPrimaryButtonText, { color: theme.colors.accentForeground }]}>
                Open Debug Auth
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.devPrimaryButton, { backgroundColor: theme.colors.accent }]}
              onPress={async () => {
                try {
                  const { seedChallenges } = require('@/lib/seedChallenges');
                  const result = await seedChallenges();
                  const msg = result.error
                    ? `Error: ${result.error}`
                    : `Seeded ${result.inserted} challenges`;
                  if (Platform.OS === 'web') alert(msg); else Alert.alert('Seed Challenges', msg);
                } catch (e: any) {
                  const msg = e.message || 'Unknown error';
                  if (Platform.OS === 'web') alert(msg); else Alert.alert('Error', msg);
                }
              }}>
              <Text style={[styles.devPrimaryButtonText, { color: theme.colors.accentForeground }]}>
                Seed 260 Weekly Challenges
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.devSecondaryButton,
                { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
              ]}
              onPress={() => router.push('/launch-qa')}>
              <Text style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                Open Launch QA
              </Text>
            </TouchableOpacity>

            {__DEV__ && (
              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={handleOpenPublicSmoke}>
                <Text style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Public Views Smoke Test
                </Text>
              </TouchableOpacity>
            )}

            <View style={[styles.devToolsGroup, { borderTopColor: theme.colors.border }]}>
              <Text style={[styles.devToolsGroupTitle, { color: theme.colors.textSecondary }]}>
                Redirect Tools
              </Text>

              <View style={styles.devInputContainer}>
                <Text style={[styles.devInputLabel, { color: theme.colors.textSecondary }]}>
                  Test Email
                </Text>
                <TextInput
                  style={[
                    styles.devInput,
                    {
                      backgroundColor: theme.colors.surfaceVariant,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                  ]}
                  value={devTestEmail}
                  onChangeText={setDevTestEmail}
                  placeholder="test@email.com"
                  placeholderTextColor={theme.colors.textTertiary}
                  autoCapitalize="none"
                  keyboardType="email-address"
                />
              </View>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={handleCopyRedirectUrl}>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Copy Redirect URL
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={() =>
                  testRedirectUrl(
                    'https://douevenlift.com/reset-password',
                    'Test: https://douevenlift.com/reset-password (canonical)'
                  )
                }>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Test Redirect: https://douevenlift.com/reset-password (canonical)
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={() =>
                  testRedirectUrl(
                    'https://www.douevenlift.com/reset-password',
                    'Test: https://www.douevenlift.com/reset-password (www)'
                  )
                }>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Test Redirect: https://www.douevenlift.com/reset-password (www)
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={() =>
                  testRedirectUrl('duel://reset-password', 'Test: duel://reset-password')
                }>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Test Redirect: duel://reset-password
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={() => {
                  const expoUrl = Linking.createURL('reset-password');
                  testRedirectUrl(expoUrl, `Test: ${expoUrl}`);
                }}>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Test Redirect: Linking.createURL(&quot;reset-password&quot;)
                </Text>
              </TouchableOpacity>
            </View>

            <View style={[styles.devToolsGroup, { borderTopColor: theme.colors.border }]}>
              <Text style={[styles.devToolsGroupTitle, { color: theme.colors.textSecondary }]}>
                Recovery Tools
              </Text>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={handleSimulateRecovery}>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Simulate Recovery
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={handleForceLocalSignOut}>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Force Local Sign Out
                </Text>
              </TouchableOpacity>

              <Text style={[styles.devNote, { color: theme.colors.textSecondary }]}>
                Invite generation is handled via the admin console.
              </Text>
            </View>

            {profile?.gym_id && (
              <View style={[styles.devToolsGroup, { borderTopColor: theme.colors.border }]}>
                <Text style={[styles.devToolsGroupTitle, { color: theme.colors.textSecondary }]}>
                  Gym Tools
                </Text>

                <TouchableOpacity
                  style={[
                    styles.devSecondaryButton,
                    { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                  ]}
                  onPress={() => router.push('/gym-join-code')}>
                  <Text
                    style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                    Show Gym Join Code
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            <View style={[styles.devToolsGroup, { borderTopColor: theme.colors.border }]}>
              <Text style={[styles.devToolsGroupTitle, { color: theme.colors.textSecondary }]}>
                Tier Simulator
              </Text>
              <Text style={[styles.devNote, { color: theme.colors.textTertiary, marginBottom: 8 }]}>
                Override subscription tier for testing. Persists across restarts.
              </Text>

              {([null, 'free', 'pro', 'coach_pro', 'org_pro'] as (Tier | null)[]).map((tier) => {
                const TIER_DISPLAY: Record<string, string> = { free: 'BASE', pro: 'PRO', coach_pro: 'COACH', org_pro: 'CLUB' };
                const label = tier === null ? 'None (use real profile)' : TIER_DISPLAY[tier] ?? tier.toUpperCase();
                const isActive = devTier === tier;
                return (
                  <TouchableOpacity
                    key={tier ?? 'none'}
                    style={[
                      styles.devSecondaryButton,
                      {
                        backgroundColor: isActive ? theme.colors.accent : theme.colors.surfaceVariant,
                        borderColor: isActive ? theme.colors.accent : theme.colors.border,
                      },
                    ]}
                    onPress={() => {
                      void devTierOverride.set(tier);
                      // Also sync legacy usePro for backwards compat
                      void setDevOverride(tier === 'pro' || tier === 'coach_pro' || tier === 'org_pro');
                    }}>
                    <Text
                      style={[
                        styles.devSecondaryButtonText,
                        { color: isActive ? theme.colors.text : theme.colors.textSecondary, fontWeight: isActive ? '800' : '600' },
                      ]}>
                      {label}{isActive ? ' ✓' : ''}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={[styles.devToolsGroup, { borderTopColor: theme.colors.border }]}>
              <Text style={[styles.devToolsGroupTitle, { color: theme.colors.textSecondary }]}>
                Asset Debug
              </Text>

              <View style={styles.assetRow}>
                <Image
                  source={ICON_IMAGE}
                  style={[styles.assetImage, { borderColor: theme.colors.border }]}
                  onLoad={() => setIconStatus('loaded')}
                  onError={(e) => {
                    setIconStatus('error');
                    setIconError(e.nativeEvent.error?.toString() || 'Unknown error');
                  }}
                />
                <Text style={[styles.assetText, { color: theme.colors.textSecondary }]}>
                  ICON: {iconStatus === 'loaded' ? 'loaded' : iconStatus === 'error' ? `error${iconError ? ` - ${iconError}` : ''}` : 'pending'}
                </Text>
              </View>

              <View style={styles.assetRow}>
                <Image
                  source={DUEL_LOGO}
                  style={[styles.assetImage, { borderColor: theme.colors.border }]}
                  onLoad={() => setDuelStatus('loaded')}
                  onError={(e) => {
                    setDuelStatus('error');
                    setDuelError(e.nativeEvent.error?.toString() || 'Unknown error');
                  }}
                />
                <Text style={[styles.assetText, { color: theme.colors.textSecondary }]}>
                  DUEL: {duelStatus === 'loaded' ? 'loaded' : duelStatus === 'error' ? `error${duelError ? ` - ${duelError}` : ''}` : 'pending'}
                </Text>
              </View>

              <View style={styles.assetRow}>
                <Image
                  source={{ uri: REMOTE_URL }}
                  style={[styles.assetImage, { borderColor: theme.colors.border }]}
                  onLoad={() => setRemoteStatus('loaded')}
                  onError={(e) => {
                    setRemoteStatus('error');
                    setRemoteError(e.nativeEvent.error?.toString() || 'Unknown error');
                  }}
                />
                <Text style={[styles.assetText, { color: theme.colors.textSecondary }]}>
                  REMOTE: {remoteStatus === 'loaded' ? 'loaded' : remoteStatus === 'error' ? `error${remoteError ? ` - ${remoteError}` : ''}` : 'pending'}
                </Text>
              </View>
            </View>

            <View style={[styles.devToolsGroup, { borderTopColor: theme.colors.border }]}>
              <Text style={[styles.devToolsGroupTitle, { color: theme.colors.textSecondary }]}>
                Push Notifications
              </Text>
              <TouchableOpacity
                style={[
                  styles.devSecondaryButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
                ]}
                onPress={() => router.push('/notification-test')}>
                <Text
                  style={[styles.devSecondaryButtonText, { color: theme.colors.textSecondary }]}>
                  Notification Test Console
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </DeveloperToolsAccordion>

        <Text style={[styles.versionText, { color: theme.colors.textTertiary }]}>
          Version {Constants.expoConfig?.version ?? '1.0.0'}
          {Constants.expoConfig?.ios?.buildNumber ? ` (${Constants.expoConfig.ios.buildNumber})` : ''}
        </Text>
      </ScrollView>

      {/* Sign Out confirmation modal (web) */}
      <Modal visible={showSignOutConfirm} transparent animationType="fade" onRequestClose={() => setShowSignOutConfirm(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Sign Out</Text>
            <Text style={[styles.modalMessage, { color: theme.colors.textSecondary }]}>Are you sure you want to sign out?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={[styles.modalBtn, { borderColor: theme.colors.border, borderWidth: 1 }]} onPress={() => setShowSignOutConfirm(false)}>
                <Text style={[styles.modalBtnText, { color: theme.colors.textSecondary }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalBtn, { backgroundColor: theme.colors.accent }]} onPress={confirmSignOut}>
                <Text style={[styles.modalBtnText, { color: theme.colors.accentForeground, fontWeight: '700' }]}>Sign Out</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Delete Account prompt modal (web) */}
      <Modal visible={showDeletePrompt} transparent animationType="fade" onRequestClose={() => setShowDeletePrompt(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Delete Account</Text>
            <Text style={[styles.modalMessage, { color: theme.colors.textSecondary }]}>Are you sure you want to delete your account? This action cannot be undone.</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={[styles.modalBtn, { borderColor: theme.colors.border, borderWidth: 1 }]} onPress={() => setShowDeletePrompt(false)}>
                <Text style={[styles.modalBtnText, { color: theme.colors.textSecondary }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalBtn, { backgroundColor: '#ff4444' }]} onPress={confirmDeletePrompt}>
                <Text style={[styles.modalBtnText, { color: '#fff', fontWeight: '700' }]}>Delete My Account</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
    </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  section: {
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  sectionIcon: {
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
  },
  themeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  themeOption: {
    borderWidth: 1.5,
    borderRadius: 2,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    gap: 8,
    minWidth: 90,
  },
  themeSwatches: {
    flexDirection: 'row',
    gap: 4,
  },
  themeSwatch: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
  themeLabel: {
    fontSize: 11,
    fontFamily: TYPO.body.semibold,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  settingInfo: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontFamily: TYPO.body.semibold,
  },
  settingDescription: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    marginTop: 2,
  },
  versionText: {
    textAlign: 'center',
    fontSize: 12,
    marginTop: 24,
  },
  devToolsSection: {
    gap: 16,
  },
  devToolsTitle: {
    fontSize: 16,
    fontWeight: '800',
  },
  devPrimaryButton: {
    paddingVertical: 14,
    borderRadius: 0,
    alignItems: 'center',
  },
  devPrimaryButtonText: {
    fontSize: 15,
    fontWeight: '700',
  },
  devToolsGroup: {
    borderTopWidth: 1,
    paddingTop: 12,
    gap: 10,
  },
  devToolsGroupTitle: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  devInputContainer: {
    gap: 6,
  },
  devInputLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  devInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  devSecondaryButton: {
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 0,
  },
  devSecondaryButtonText: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  devNote: {
    fontSize: 12,
    lineHeight: 16,
    textAlign: 'center',
  },
  devToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  devToggleInfo: {
    flex: 1,
  },
  devToggleLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  devToggleValue: {
    fontSize: 12,
    marginTop: 4,
  },
  assetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  assetImage: {
    width: 28,
    height: 28,
    borderWidth: 1,
    borderRadius: 4,
  },
  assetText: {
    fontSize: 12,
    flex: 1,
  },
  planPicker: {
    paddingTop: 12,
    gap: 8,
  },
  planPickerTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 4,
  },
  planCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
  },
  planName: {
    fontSize: 15,
    fontFamily: TYPO.heading.semibold,
  },
  planDesc: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
    marginTop: 2,
  },
  planPrice: {
    fontSize: 14,
    fontFamily: TYPO.heading.bold,
  },
  planAnnual: {
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
  },
  planCurrent: {
    fontSize: 11,
    marginTop: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    borderRadius: 2,
    padding: 24,
    width: '100%',
    maxWidth: 360,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 0,
    alignItems: 'center',
  },
  modalBtnText: {
    fontSize: 15,
    fontWeight: '600',
  },
});
